import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import PageLayout from "../components/PageLayout";
import PageHero from "../components/PageHero";
import ContactCTA from "../components/ContactCTA";
import { blogPosts } from "../data/mock";
import { Calendar, User, Clock, ArrowUpRight, ArrowLeft } from "lucide-react";

const BlogDetail = () => {
  const { slug } = useParams();
  const post = blogPosts.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <PageLayout>
      <PageHero
        badge={post.category}
        title={post.title}
        breadcrumbs={[
          { label: "Blog", to: "/blog" },
          { label: post.title },
        ]}
      />

      {/* Article */}
      <section className="py-20 bg-white">
        <article className="max-w-[820px] mx-auto px-6">
          {/* Meta */}
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500 mb-8 pb-6 border-b border-slate-100">
            <span className="flex items-center gap-1.5"><User size={14} /> {post.author}</span>
            <span className="flex items-center gap-1.5"><Calendar size={14} /> {post.date}</span>
            <span className="flex items-center gap-1.5"><Clock size={14} /> {post.readTime}</span>
          </div>

          {/* Hero image */}
          <div className="relative aspect-[16/9] rounded-3xl overflow-hidden mb-10 shadow-2xl shadow-emerald-500/10">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>

          {/* Body */}
          <div className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-5">
            <p className="text-lg text-slate-600 leading-relaxed">
              {post.excerpt}
            </p>
            <p>
              Wire and cable selection looks simple from the outside — pick a gauge, pick an
              insulation, pull it through the conduit. In practice, the right choice depends on
              dozens of variables: temperature, flexibility, abrasion, chemical exposure, signal
              integrity, regulatory listings, and total cost over the life of the install.
            </p>
            <h2 className="text-2xl font-bold pt-4" style={{ color: "#0a2e1f" }}>
              Start with the environment
            </h2>
            <p>
              Every job starts with the environment the cable is going to live in. Indoor office
              runs are gentle. Outdoor coastal installs in Hawaii are not — humidity, UV, salt
              spray and temperature swings all chip away at insulation that wasn't designed for
              them. The first question we ask any client is: <em>where is this cable going to
              spend the next 20 years?</em>
            </p>
            <h2 className="text-2xl font-bold pt-4" style={{ color: "#0a2e1f" }}>
              Match the spec to the use case
            </h2>
            <p>
              Once the environment is clear, the spec usually narrows down quickly. For a typical
              commercial run, a UL-listed THHN/THWN-2 in the right gauge is hard to beat. For
              defense work, MIL-W-22759 family wires offer better temperature ratings and chemical
              resistance — at a higher cost. For data, the choice between copper and fiber comes
              down to distance, bandwidth and EMI exposure.
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Always check the certifications required by the AHJ before quoting.</li>
              <li>Don't oversize on temperature rating "just in case" — it costs real money.</li>
              <li>For high-flex applications, stranding matters more than gauge.</li>
              <li>Document everything: part numbers, lot codes, install dates.</li>
            </ul>
            <h2 className="text-2xl font-bold pt-4" style={{ color: "#0a2e1f" }}>
              When in doubt, call us
            </h2>
            <p>
              Our team has spec'd thousands of jobs across Hawaii and the Pacific. If you're
              unsure which product fits your application, send us the details — we'll come back
              with two or three options and the trade-offs between them.
            </p>
          </div>

          {/* Back link */}
          <div className="mt-12 pt-8 border-t border-slate-100">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 hover:gap-3 transition-all"
            >
              <ArrowLeft size={15} /> Back to all articles
            </Link>
          </div>
        </article>
      </section>

      {/* Related */}
      <section className="py-20" style={{ backgroundColor: "#f7faf8" }}>
        <div className="max-w-[1200px] mx-auto px-6">
          <h2 className="text-2xl md:text-3xl font-bold mb-10 tracking-tight" style={{ color: "#0a2e1f" }}>
            Continue reading
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {related.map((p) => (
              <Link
                key={p.id}
                to={`/blog/${p.slug}`}
                className="group block bg-white rounded-3xl overflow-hidden border border-slate-100 hover:border-emerald-200 hover:shadow-xl hover:shadow-emerald-500/10 hover:-translate-y-1 transition-all duration-500"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-3 text-xs text-slate-500 mb-2">
                    <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 font-bold uppercase text-[10px] tracking-wider">{p.category}</span>
                    <span>{p.readTime}</span>
                  </div>
                  <h3 className="text-base font-bold mb-2 leading-snug group-hover:text-emerald-700 transition-colors" style={{ color: "#0a2e1f" }}>
                    {p.title}
                  </h3>
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    Read more <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ContactCTA />
    </PageLayout>
  );
};

export default BlogDetail;
